
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, RefreshCw, Database, CheckCircle, XCircle, Clock } from "lucide-react";
import { MedicalKnowledgeService, SyncStatus } from "@/services/medicalKnowledge";
import { useToast } from "@/hooks/use-toast";

export const MedicalDataSync: React.FC = () => {
  const [syncStatuses, setSyncStatuses] = useState<SyncStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    loadSyncStatuses();
  }, []);

  const loadSyncStatuses = async () => {
    try {
      const statuses = await MedicalKnowledgeService.getSyncStatus();
      setSyncStatuses(statuses);
    } catch (error) {
      console.error('Error loading sync statuses:', error);
      toast({
        title: "Error",
        description: "Failed to load sync statuses",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async (apiSource: 'pubmed' | 'trip' | 'openfda' | 'dailymed' | 'clinicaltrials' | 'icd11') => {
    setSyncing(apiSource);
    
    try {
      const success = await MedicalKnowledgeService.triggerSync(apiSource);
      
      if (success) {
        toast({
          title: "Sync Started",
          description: `${apiSource.toUpperCase()} sync has been initiated`,
        });
        
        // Refresh status after a delay
        setTimeout(() => {
          loadSyncStatuses();
        }, 2000);
      } else {
        throw new Error('Sync failed');
      }
    } catch (error) {
      toast({
        title: "Sync Failed",
        description: `Failed to start ${apiSource.toUpperCase()} sync`,
        variant: "destructive"
      });
    } finally {
      setSyncing(null);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'running':
        return <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />;
      default:
        return <Clock className="h-4 w-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'running':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          <span>Loading sync status...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Database className="h-6 w-6" />
            Medical Knowledge Base
          </h2>
          <p className="text-gray-600 mt-1">
            Sync medical data from PubMed, TRIP, OpenFDA, DailyMed, ClinicalTrials, and WHO ICD-11 APIs
          </p>
        </div>
        <Button 
          onClick={loadSyncStatuses}
          variant="outline"
          size="sm"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        {syncStatuses.map((status) => (
          <Card key={status.api_source}>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center justify-between">
                {status.api_source.toUpperCase()}
                {getStatusIcon(status.sync_status)}
              </CardTitle>
              <CardDescription>
                {status.api_source === 'pubmed' && 'Medical research articles'}
                {status.api_source === 'trip' && 'Clinical guidelines & evidence'}
                {status.api_source === 'openfda' && 'Drug safety & labeling data'}
                {status.api_source === 'dailymed' && 'FDA drug information & safety'}
                {status.api_source === 'clinicaltrials' && 'Clinical trial studies'}
                {status.api_source === 'icd11' && 'WHO standardized condition codes'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Status:</span>
                <Badge className={getStatusColor(status.sync_status)}>
                  {status.sync_status}
                </Badge>
              </div>
              
              {status.records_synced !== null && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Records:</span>
                  <span className="text-sm font-medium">{status.records_synced}</span>
                </div>
              )}
              
              {status.last_sync_at && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Last sync:</span>
                  <span className="text-sm">
                    {new Date(status.last_sync_at).toLocaleDateString()}
                  </span>
                </div>
              )}
              
              {status.error_message && (
                <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
                  {status.error_message}
                </div>
              )}
              
              <Button
                onClick={() => handleSync(status.api_source as 'pubmed' | 'trip' | 'openfda' | 'dailymed' | 'clinicaltrials' | 'icd11')}
                disabled={syncing === status.api_source || status.sync_status === 'running'}
                className="w-full"
                size="sm"
              >
                {syncing === status.api_source ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Starting...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Sync Now
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};
